/*
 *
 * Copyright (c) 2025 Alexander Orlov.
 * 34 Middletown Ave, Atlantic Highlands, NJ 07716
 *
 * THIS SOFTWARE IS THE CONFIDENTIAL AND PROPRIETARY INFORMATION OF
 * Alexander Orlov ("CONFIDENTIAL INFORMATION").
 *
 * Use, reproduction, modification, or distribution of this code
 * is permitted only pursuant to a written license expressly granted
 * by Alexander Orlov.
 *
 * You shall not disclose such confidential information and shall use
 * it only in accordance with the terms of the written license agreement
 * you have entered into with Alexander Orlov.
 *
 * Author: Alexander Orlov
 * Aegis AO Soft
 *
 */


import apiClient from './client';
import {
  APIResponse,
  PendingPaymentDTO,
  VerraTollDailyInvoiceDTO,
  VerraTollDailyPaymentInfoRequest,
  OwnerChargeDTO,
  ChargeDetailDTO,
  OwnerTollDTO,
  PaymentStatusInfoDTO,
} from '@/types';

/**
 * Outcome of settling a charge outside the card. The server is the authority on the split: our fee is taken
 * out of the received cash first and billed to the owner, the remainder is the owner's, and `shortfall` is the
 * discount the owner granted the driver (absorbed by the owner).
 */
export interface CashSettlementResult {
  success: boolean;
  message?: string;
  amountOwed: number;
  cashReceived: number;
  ourFee: number;
  ownerKeeps: number;
  shortfall: number;
}

export const paymentsApi = {
  // Get pending payments (used for Payment Status column in listings)
  getPendingPayments: async (userId: string): Promise<PendingPaymentDTO[]> => {
    const response = await apiClient.get<APIResponse<PendingPaymentDTO[]>>(
      `/TollPayment/get-pending-payments/${userId}`
    );
    return response.data.result || [];
  },

  // Get failed toll payments (grouped by vehicle with driver info)
  getFailedTollPayments: async (ownerId: string): Promise<VerraTollDailyPaymentInfoRequest[]> => {
    const response = await apiClient.get<APIResponse<VerraTollDailyPaymentInfoRequest[]>>(
      `/TollPayment/get-failed-payments/${ownerId}`
    );
    return response.data.result || [];
  },

  // Get successful toll payments
  getSuccessfulTollPayments: async (
    ownerId: string,
    startDate?: string,
    endDate?: string
  ): Promise<VerraTollDailyInvoiceDTO[]> => {
    const response = await apiClient.post<APIResponse<VerraTollDailyInvoiceDTO[]>>(
      '/TollPayment/get-successful-payments',
      {
        ownerId,
        startDate: startDate || new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString(),
        endDate: endDate || new Date().toISOString(),
      }
    );
    return response.data.result || [];
  },

  // Process multiple toll payments
  processMultipleTollPayments: async (
    ownerId: string,
    dailyPaymentIds: string[],
    paidBy: 'owner' | 'renter',
    renterId?: string
  ): Promise<APIResponse<{ processed: number; failed: number }>> => {
    const response = await apiClient.post<APIResponse<{ processed: number; failed: number }>>(
      '/TollPayment/process-multiple-payments',
      {
        ownerId,
        dailyPaymentIds,
        paidBy,
        renterId,
      }
    );
    return response.data;
  },

  // Get owner toll charges (payment-level, grouped)
  getOwnerCharges: async (
    from?: string,
    to?: string,
    search?: string,
    userId?: string,
    bookingId?: string
  ): Promise<OwnerChargeDTO[]> => {
    const params = new URLSearchParams();
    if (from) params.append('dateFrom', from);
    if (to) params.append('dateTo', to);
    if (search) params.append('search', search);
    if (userId) params.append('userId', userId);
    if (bookingId) params.append('bookingId', bookingId);
    const response = await apiClient.get<APIResponse<OwnerChargeDTO[]>>(
      `/TollPayment/get-owner-charges?${params.toString()}`
    );
    return response.data.result || [];
  },

  // Find owner charge by payment ID (no date filter)
  findOwnerCharge: async (paymentId: string): Promise<OwnerChargeDTO[]> => {
    const response = await apiClient.get<APIResponse<OwnerChargeDTO[]>>(
      `/TollPayment/find-owner-charge?paymentId=${encodeURIComponent(paymentId)}`
    );
    return response.data.result || [];
  },

  // Get Stripe receipt URL for a charge
  getReceiptUrl: async (paymentId: string): Promise<string | null> => {
    try {
      const response = await apiClient.get<APIResponse<{ receiptUrl: string }>>(
        `/TollPayment/receipt-url/${paymentId}`
      );
      return response.data.result?.receiptUrl || null;
    } catch {
      return null;
    }
  },

  // Retry a failed/pending toll charge (owner-initiated)
  retryCharge: async (paymentId: string): Promise<{ success: boolean; receiptUrl?: string; message?: string }> => {
    const response = await apiClient.post<APIResponse<{ success: boolean; receiptUrl?: string }>>(
      `/TollPayment/retry-charge/${encodeURIComponent(paymentId)}`
    );
    // Backend returns HTTP 200 with Reason=None + message on failure (see ResultExtensions.ToResult),
    // so axios does NOT throw. The failure reason is on response.data.message, not in result.
    const result = response.data.result;
    if (result && result.success) return result;
    return {
      success: false,
      message: response.data.message || undefined,
    };
  },

  /**
   * Settle a charge the card could not collect because the owner took the money from the driver by hand.
   * `cashAmount` is what was ACTUALLY received — it may be less than owed when a discount was agreed. Our fee
   * comes out of that first and is billed to the owner; the rest of the toll stays with the owner.
   */
  settleChargeByCash: async (
    paymentId: string,
    body: { cashAmount: number; method?: string; note?: string }
  ): Promise<CashSettlementResult> => {
    const response = await apiClient.post<APIResponse<CashSettlementResult>>(
      `/TollPayment/settle-cash/${encodeURIComponent(paymentId)}`,
      body
    );
    // Same envelope quirk as retryCharge: a refusal comes back as HTTP 200 with the reason on `message`.
    const result = response.data.result;
    if (result) return result;
    return { success: false, message: response.data.message || undefined } as CashSettlementResult;
  },

  // Get failed / pending-blocking payments for a booking, each with full PaymentStatusInfo.
  // Uses a direct DB query (no invoice join) so it includes payments with no invoice rows.
  getFailedPaymentsByBooking: async (bookingId: string): Promise<PaymentStatusInfoDTO[]> => {
    const response = await apiClient.get<APIResponse<PaymentStatusInfoDTO[]>>(
      `/TollPayment/failed-payments-by-booking/${encodeURIComponent(bookingId)}`
    );
    return response.data.result || [];
  },

  // Get rich payment status info (DB row + Stripe PaymentIntent / last_payment_error)
  getPaymentStatusInfo: async (paymentId: string): Promise<PaymentStatusInfoDTO | null> => {
    try {
      const response = await apiClient.get<APIResponse<PaymentStatusInfoDTO>>(
        `/TollPayment/payment-status-info/${encodeURIComponent(paymentId)}`
      );
      return response.data.result || null;
    } catch {
      return null;
    }
  },

  // Get charge details (invoice-level rows for a payment, for modal)
  getChargeDetails: async (paymentId: string): Promise<ChargeDetailDTO[]> => {
    const response = await apiClient.get<APIResponse<ChargeDetailDTO[]>>(
      `/TollPayment/get-charge-details/${paymentId}`
    );
    return response.data.result || [];
  },

  // Pay a single toll (renter pays outstanding toll)
  paySingleToll: async (dailyPaymentId: string, paymentMethodId?: string): Promise<{ success: boolean; receiptUrl?: string }> => {
    const response = await apiClient.post<APIResponse<{ success: boolean; receiptUrl?: string }>>(
      '/TollPayment/pay-single-toll',
      { dailyPaymentId, paymentMethodId }
    );
    return response.data.result || { success: false };
  },

  // Pay all unpaid tolls
  payAllTolls: async (paymentMethodId?: string): Promise<{ success: boolean; paid: number; failed: number }> => {
    const response = await apiClient.post<APIResponse<{ success: boolean; paid: number; failed: number }>>(
      '/TollPayment/pay-all-tolls',
      { paymentMethodId }
    );
    return response.data.result || { success: false, paid: 0, failed: 0 };
  },

  // Get renter unpaid toll summary
  getRenterUnpaidSummary: async (): Promise<{ totalUnpaid: number; count: number }> => {
    const response = await apiClient.get<APIResponse<{ totalUnpaid: number; count: number }>>(
      '/TollPayment/get-renter-unpaid-summary'
    );
    return response.data.result || { totalUnpaid: 0, count: 0 };
  },

  // Get renter tolls (tolls charged to the current renter)
  getRenterTolls: async (
    from?: string,
    to?: string,
    search?: string,
  ): Promise<OwnerTollDTO[]> => {
    const params = new URLSearchParams();
    if (from) params.append('dateFrom', from);
    if (to) params.append('dateTo', to);
    if (search) params.append('search', search);
    const response = await apiClient.get<APIResponse<OwnerTollDTO[]>>(
      `/TollPayment/get-renter-tolls?${params.toString()}`
    );
    return response.data.result || [];
  },

  // Get owner tolls (invoice-level, not grouped)
  getOwnerTolls: async (
    from?: string,
    to?: string,
    search?: string,
    userId?: string
  ): Promise<OwnerTollDTO[]> => {
    const params = new URLSearchParams();
    if (from) params.append('dateFrom', from);
    if (to) params.append('dateTo', to);
    if (search) params.append('search', search);
    if (userId) params.append('userId', userId);
    const response = await apiClient.get<APIResponse<OwnerTollDTO[]>>(
      `/TollPayment/get-owner-tolls?${params.toString()}`
    );
    return response.data.result || [];
  },
};
